import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  AtSign,
  MessageSquare,
  Users,
  UserCheck,
  UserPlus,
  Info,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, type NotificationTypeKey } from '@/api/notifications';
import { useNotificationStore } from '@/store/notificationStore';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { getNotificationDestination } from '@/lib/notificationDestination';
import { useMobile } from '@/pages/chat/ChatPage';

const TYPE_ICONS: Partial<Record<NotificationTypeKey, LucideIcon>> = {
  MENTION: AtSign,
  DM: MessageSquare,
  CHANNEL_INVITE: Users,
  FRIEND_REQUEST: UserPlus,
  FRIEND_ACCEPTED: UserCheck,
  SYSTEM: Info,
};

const TYPE_COLORS: Partial<Record<NotificationTypeKey, string>> = {
  MENTION: 'text-yellow-400 bg-yellow-900/30',
  DM: 'text-blue-400 bg-blue-900/30',
  CHANNEL_INVITE: 'text-green-400 bg-green-900/30',
  FRIEND_REQUEST: 'text-brand-300 bg-brand-900/30',
  FRIEND_ACCEPTED: 'text-green-300 bg-green-900/30',
  SYSTEM: 'text-purple-400 bg-purple-900/30',
};

export default function NotificationPanel() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { setShowSidebar } = useMobile();
  const { notifications, unreadCount, setNotifications, setUnreadCount, markRead, markAllRead, removeNotification } =
    useNotificationStore();

  // Fetch notifications on mount & when panel opens
  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list({ limit: 30 }),
    staleTime: 30_000,
  });

  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (data) setNotifications(data.data);
  }, [data, setNotifications]);

  useEffect(() => {
    if (countData !== undefined) setUnreadCount(countData);
  }, [countData, setUnreadCount]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const markReadMut = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: (_, id) => {
      markRead(id);
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const markAllMut = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      markAllRead();
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => notificationsApi.remove(id),
    onSuccess: (_, id) => {
      removeNotification(id);
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const handleClick = (notif: (typeof notifications)[0]) => {
    if (!notif.readAt) markReadMut.mutate(notif.id);

    const destination = getNotificationDestination(notif.type, notif.metadata);
    if (destination) {
      if (notif.type === 'FRIEND_REQUEST' || notif.type === 'FRIEND_ACCEPTED') {
        qc.invalidateQueries({ queryKey: ['friends'] });
      }
      setShowSidebar(false);
      navigate(destination);
    }
    setOpen(false);
  };

  return (
    <div ref={panelRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        title="Уведомления"
        aria-label="Уведомления"
        aria-expanded={open}
        className="w-9 h-9 rounded-full bg-surface-800 text-surface-400 hover:bg-surface-700 hover:text-surface-200 flex items-center justify-center transition-colors flex-shrink-0 relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        <Bell size={15} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-9 w-80 max-h-[70vh] bg-surface-900 border border-surface-700 rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-surface-800">
            <span className="text-sm font-semibold text-surface-100">Уведомления</span>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllMut.mutate()}
                className="text-xs text-brand-400 hover:text-brand-300 flex items-center gap-1 transition-colors"
              >
                <CheckCheck size={12} />
                Прочитать все
              </button>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell size={28} className="mx-auto mb-2 text-surface-700" />
                <p className="text-surface-500 text-sm">Нет уведомлений</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = TYPE_ICONS[notif.type] ?? Info;
                const colorClass = TYPE_COLORS[notif.type] ?? TYPE_COLORS.SYSTEM ?? '';
                return (
                  <div
                    key={notif.id}
                    className={`flex items-stretch border-b border-surface-800/50 transition-colors hover:bg-surface-800 ${
                      !notif.readAt ? 'bg-surface-850' : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleClick(notif)}
                      aria-label={`${notif.title}. ${notif.body}`}
                      className="flex min-w-0 flex-1 items-start gap-3 px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400"
                    >
                      <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${colorClass}`}>
                        <Icon size={14} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className={`truncate text-xs font-medium ${!notif.readAt ? 'text-surface-100' : 'text-surface-300'}`}>
                            {notif.title}
                          </span>
                          {!notif.readAt && (
                            <span className="h-2 w-2 flex-shrink-0 rounded-full bg-brand-500" />
                          )}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-surface-500">{notif.body}</span>
                        <span className="mt-1 block text-[10px] text-surface-600">
                          {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true, locale: ru })}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteMut.mutate(notif.id)}
                      aria-label="Удалить уведомление"
                      className="my-2 mr-2 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-surface-600 transition-colors hover:bg-surface-900 hover:text-red-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
